/**
 * useMatchGroups — bridge data hook for Gutschrift-Check (issue #673).
 *
 * Sub-13 of #632 rewrite: consumes ``gutschrift_match_group`` derived
 * facts (produced by the worker's apps-layer materialiser, sub-11) for
 * the structural decisions — group composition, status, line pairing,
 * operator disposition — and hydrates the rich ``MatchGroup`` shape on
 * top via batched event + event_line fetches. The dedupe / client-side
 * join / status-derivation / packaging-kolli logic that used to live
 * here is gone: the engine writes one content-addressed fact per
 * group, the FE just renders.
 *
 * The ``MatchGroup`` TypeScript shape is preserved so
 * ``MatchGraph.tsx`` / ``FilterBar.tsx`` / ``GutschriftCheckPage.tsx`` /
 * ``LineRow.tsx`` / ``priceIndex.ts`` need no changes.
 */
import { useQuery } from '@tanstack/react-query';
import { twinService, type DerivedFact } from '../services/twinService';
import { fetchAllPages } from '../services/paging';

// ---------------------------------------------------------------------------
// Types — unchanged from pre-sub-13 to keep consumer contracts stable.
// ---------------------------------------------------------------------------

/**
 * HOF-215 AC 7 — ``price_missing`` is a FOURTH status, not a flavour of
 * ``quantity_mismatch``, because the SUM-aggregate pass (#1444) exists to clear
 * a quantity deviation once the counterpart quantities reconcile — and on the
 * EUR 907,50 shape they reconcile perfectly. Folding it into
 * ``quantity_mismatch`` would have that pass silently clear the one signal the
 * group carries.
 */
export type MatchLineStatus = 'matched' | 'quantity_mismatch' | 'price_missing' | 'unmatched';

/** Worst-of ranking for a line touched by several pairs. Money outranks quantity. */
const LINE_STATUS_RANK: Record<MatchLineStatus, number> = {
  matched: 0,
  quantity_mismatch: 1,
  price_missing: 2,
  unmatched: 3,
};

export interface MatchGroupLine {
  lineId: string;
  eventId: string;
  side: 'delivery' | 'settlement';
  productName: string;
  quantity: number | null;
  quantityUnit: string | null;
  unitPrice: number | null;
  amount: number | null;
  matchStatus: MatchLineStatus;
  // First counterpart (``matchedLineIds[0]``) — kept for consumers
  // that only ever need one partner. Under full N:M (#1291 + #1391)
  // EITHER side may carry several, so the singular is exact only for
  // 1:1 lines.
  matchedLineId: string | null;
  // #1291 + #1391 N:M fan-out — EVERY counterpart line id, in
  // ``line_pairs`` append order. ``[]`` when unpaired. A delivery line
  // may carry N entries (one LS line settled by several GS lines,
  // #1291) AND a settlement line may carry N entries (one GS line
  // aggregating several LS lines, #1391).
  matchedLineIds: string[];
  quantityDelta: number | null;
  resourceCategory: string | null;
  rate: number | null;
  palletCount: number | null;
  packaging: string | null;
  resolvedKolliCount: number | null;
  resolvedKolliUnit: string | null;
  // #793 phase 2 — normalised POS token this LS line cites (the GS
  // POS the resale-pattern line names, e.g. ``"503685/2026"``). Drives
  // MatchGraph's per-line → placeholder-GS dotted arrow when the
  // token is in the group's ``expectedSettlementPosTokens`` list.
  // ``null`` on lines that don't name a GS POS (the common non-resale
  // case) and on every settlement-side line (symmetric back-edge is
  // an LS-side property — settlement lines never carry it).
  referencedSettlementPos: string | null;
}

export type MatchGroupStatus = 'matched' | 'partial' | 'orphan_delivery' | 'orphan_settlement';

/**
 * Compact summary of a ``sales_agreement`` entity hydrated onto the
 * group via ``fact_value.agreement_ids``. The full attribute set isn't
 * needed for the BESTELLUNG canvas column — agreement_number (the
 * BestellNr the operator sees on the source GS/LS) and the friendly
 * ``name`` are the only fields rendered today. Issue #784.
 */
export interface BestellungSummary {
  entityId: string;
  agreementNumber: string | null;
  name: string | null;
  supplierName: string | null;
  buyerName: string | null;
  // #1812 (FE) — the SINGULAR order-doc provenance cursor written by the
  // linker and preserved by subtask 07's ontology declaration (PR #1818).
  // Non-null ⇒ the BESTELLUNG tile is clickable and opens the customer
  // order in the DocumentPreviewPanel. Read from the entity's
  // ``source_doc_id`` attribute ONLY — never the plural ``source_doc_ids``,
  // which is polluted with the citing GS/LS doc on stub-minted rows.
  sourceDocId: string | null;
}

/**
 * Compact summary of a ``payout_statement_event`` (Periodenabrechnung,
 * #793 phase 3) hydrated for the ABRECHNUNG canvas column. The
 * materialiser surfaces matching ids on ``fact_value.payout_statement_ids``;
 * this hook joins them against the paginated entity list. v1 renders
 * the header number + total amount only — the full attribute set lives
 * on the source doc.
 */
export interface PayoutStatementSummary {
  entityId: string;
  number: string | null;
  totalAmount: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  issuerName: string | null;
  recipientName: string | null;
}

/**
 * Compact summary of a ``payout_advice_event`` (Zahlungsavis, #793
 * phase 4) hydrated for the ZAHLUNGSAVIS canvas column. The
 * materialiser surfaces matching ids on ``fact_value.payout_advice_ids``;
 * this hook joins them against the paginated entity list. ``number``
 * is the optional Avis-Nr.; some senders (FRUTANiA 20260520) emit a
 * Konto-Nr. instead. ``totalPaymentAmount`` is the announced bank-
 * transfer total.
 */
export interface PayoutAdviceSummary {
  entityId: string;
  number: string | null;
  totalPaymentAmount: number | null;
  paymentDueDate: string | null;
  buyerName: string | null;
  sellerName: string | null;
  // #793 phase 4 codex P1 — the document_number values inside
  // ``referenced_documents``. The materialiser joins the advice to
  // the component when ANY GS's credit_note_number is in this list;
  // MatchGraph re-uses the same key to pick the correct GS card as
  // the arrow source in a multi-GS component (falling back to the
  // first GS card when the FE-side mapping can't be resolved — the
  // server already validated at least one match exists).
  referencedCreditNoteNumbers: string[];
}

/**
 * Conflict surfaced by the materialiser when ≥2 distinct
 * ``payout_statement_event`` rows claim the same ``abrechnungs_number``.
 * The FE renders this as a warning on the affected match-group row;
 * the GS still draws an arrow to each candidate Abrechnung tile so the
 * operator can see which rows collide. Subtask 18 §B success criterion.
 */
export interface PayoutStatementConflict {
  abrechnungsNumber: string;
  payoutStatementIds: string[];
}

export interface MatchGroup {
  id: string;
  deliveryEvent: Record<string, unknown> | null;
  settlementEvent: Record<string, unknown> | null;
  deliveryLines: MatchGroupLine[];
  settlementLines: MatchGroupLine[];
  // Every event in the component, not just the lex-smallest primary.
  // Multi-GS groups (one LS receiving N Gutschrifts, or one GS spanning
  // N Lieferscheine) need the full set so per-event aggregations — e.g.
  // the dashboard's "GS with zero matched lines contributes its full
  // total to Lieferschein fehlt" rule — can read each event's
  // ``total_amount`` directly. Primary ``settlementEvent`` /
  // ``deliveryEvent`` stay for rendering paths that only show one card
  // per side.
  allSettlementEvents: Record<string, unknown>[];
  allDeliveryEvents: Record<string, unknown>[];
  // Hydrated ``sales_agreement`` rows referenced by the materialiser's
  // ``fact_value.agreement_ids``. Empty when no agreement is linked —
  // BESTELLUNG column is not rendered in that case (#784 acceptance).
  bestellung: BestellungSummary[];
  // #793 phase 4 — Abrechnung (column 4) and Zahlungsavis (column 5).
  //
  // ``payoutStatements`` — Periodenabrechnung rows the materialiser
  // joined via ``settlement_event.abrechnungs_number ==
  // payout_statement_event.number``.
  // ``expectedAbrechnungsNumbers`` — every abrechnungs_number cited by
  // a GS in the component, whether or not an entity matched. MatchGraph
  // diffs the two: matched → real PayoutStatementRow, unmatched →
  // strict-typed missing-doc placeholder (§A.7).
  // ``payoutAdvices`` — Zahlungsavis rows joined via
  // ``payout_advice_event.referenced_documents[*].document_number ==
  // settlement_event.credit_note_number``. No placeholder for
  // unmatched (real-only per §E.5).
  // ``payoutStatementConflicts`` — surfaced when two distinct
  // payout_statement_event rows claim the same abrechnungs_number;
  // FE renders a warning chip (no silent merge per §B).
  payoutStatements: PayoutStatementSummary[];
  expectedAbrechnungsNumbers: string[];
  payoutAdvices: PayoutAdviceSummary[];
  payoutStatementConflicts: PayoutStatementConflict[];
  status: MatchGroupStatus;
  // #1289 — the engine's per-group money block, camelCased from
  // ``fact_value.money``. The feed view's per-side money chips read it
  // (LS-side money is otherwise unobtainable: delivery events carry no
  // ``total_amount``). ``null`` for legacy facts that pre-date the
  // field.
  money: {
    settlementTotal: number;
    deliveryTotal: number;
    delta: number;
    currency: string;
    // HOF-215 AC 6 — when true the delivery side states no money at all, so
    // ``delta`` equals the whole Gutschrift and is NOT a deviation. Consumers
    // render "nicht vergleichbar" rather than a five-figure loss.
    //
    // Optional in the TYPE, always populated by the hook: it is purely
    // informational, so a caller assembling a `MatchGroup` by hand (the
    // placeholder builders and their fixtures) should not have to state a
    // position on it. Read it as ``=== true``, never as truthy-or-absent.
    deliveryUnpriced?: boolean;
  } | null;
  // HOF-215 AC 9 — the HOF-90 degraded-close vocabulary, straight off the fact.
  // Rendered as a badge on the group so the residue is visible to whoever
  // reconciles the month. Empty for a healthy group; optional for the same
  // reason as ``deliveryUnpriced`` above.
  degradedReasons?: string[];
  // Always 1 post-sub-13: the engine's ``derived_fact_id`` is content-
  // addressed on the event set, so duplicate-upload collapse happens
  // server-side. Field stays for consumer backwards-compatibility.
  duplicateCount: number;
  manuallyResolvedAt: string | null;
  // #793 phase 2 — sorted, deduped, normalised POS tokens cited by LS
  // event_lines in this group that no settlement in the component
  // matched (set difference: ``encountered − resolved``). Empty for
  // ``matched`` groups (a non-empty list demotes ``matched`` →
  // ``partial`` upstream in the materialiser) and for orphan_settlement
  // groups (no deliveries → no event_lines walked → no tokens). Drives
  // MatchGraph's typed placeholder GS tiles ("Gutschrift POS <token> ·
  // (fehlt)") on orphan_delivery and partial groups.
  expectedSettlementPosTokens: string[];
  // HOF-219 — the typed (GS, LS) DOCUMENT edges the materialiser resolved.
  // The canvas draws its own GS→LS arrow from ``delivery_reference`` and does
  // not need this to position anything; it needs it to name the LINK KIND in
  // the sever confirmation. A ``native`` edge is an exact shared-line-ref join
  // from wibill, so an operator overruling one is overruling the source system
  // and the copy has to say so (spec Risks, assumption 5). Empty for legacy
  // facts that pre-date the field.
  //
  // OPTIONAL on the interface, always set by the composer. The 22 existing
  // fixture builders across this surface's suites each construct a MatchGroup
  // literal, and a required field would have made this PR edit all of them —
  // churn on files another train issue is also editing, to buy a compile-time
  // reminder the composer does not need. Every reader coalesces with ``?? []``.
  gsLsEdges?: GsLsDocumentEdge[];
}

/** One typed document edge, camelCased from ``fact_value.gs_ls_edges``. */
export interface GsLsDocumentEdge {
  settlementEventId: string;
  deliveryEventId: string;
  linkKind: string;
}

interface GutschriftMatchGroupFactValue {
  group_id: string;
  status: MatchGroupStatus;
  period_ym: string;
  // ``agreement_id`` is the lex-smallest legacy field; ``agreement_ids``
  // (added by the materialiser to handle multi-agreement components,
  // see ``gutschrift_match_group.py:155-175``) is the canonical list.
  // The hook joins ``agreement_ids`` against ``sales_agreement`` rows
  // to hydrate the BESTELLUNG column (#784). Optional because facts
  // written before the materialiser added the list field carry only
  // the legacy singleton — the composer falls back to ``[]``.
  agreement_id: string | null;
  agreement_ids?: string[];
  settlement_event_ids: string[];
  delivery_event_ids: string[];
  line_pairs: Array<{
    delivery_line_id: string | null;
    settlement_line_id: string | null;
    match_kind: string;
    quantity_delta: number | null;
    unit: string | null;
    // HOF-215 — which pass produced this edge. Present only on pairs the
    // deterministic matcher made, so a rollback cleanup is a query rather than a
    // recomputation: a mixed group keeps the model's fact-level
    // ``model_version``, which cannot identify them.
    produced_by?: string;
  }>;
  orphan_lines: Array<{
    side: 'delivery' | 'settlement';
    event_id: string | null;
    line_id: string;
    reason: string;
    rejection_score: number | null;
  }>;
  money: {
    settlement_total: number;
    delivery_total: number;
    delta: number;
    currency: string;
    // HOF-215 AC 6 — the delivery states no money anywhere, so ``delta`` is the
    // whole Gutschrift and means nothing. Optional: legacy facts pre-date it.
    delivery_unpriced?: boolean;
  };
  // HOF-90 + HOF-215 AC 9 — why this group closed the way it did. Present on
  // the fact since HOF-90 and mapped nowhere until now.
  degraded_reasons?: string[];
  manually_resolved_at: string | null;
  manually_resolved_by: string | null;
  // #793 phase 2 — normalised POS tokens cited by LS event_lines in
  // this component that no settlement in the component matched.
  // Optional because facts written before phase 2 carry no such field
  // (composer falls back to ``[]``).
  expected_settlement_pos_tokens?: string[];
  // HOF-219 — typed (GS, LS) document edges. Optional: facts written before
  // #841 carry none.
  gs_ls_edges?: Array<{
    settlement_event_id: string;
    delivery_event_id: string;
    link_kind: string;
  }>;
  // #793 phase 4 — Abrechnung + Zahlungsavis joins.
  // All four optional because facts written before phase 4 carry none
  // of them (composer falls back to ``[]``).
  payout_statement_ids?: string[];
  expected_abrechnungs_numbers?: string[];
  payout_advice_ids?: string[];
  payout_statement_conflicts?: Array<{
    abrechnungs_number: string;
    payout_statement_ids: string[];
  }>;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const matchGroupKeys = {
  all: ['matchGroups'] as const,
  byMonth: (tenantMonth: string) => ['matchGroups', tenantMonth] as const,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const LINES_BATCH_SIZE = 200;
const LINES_PAGE_SIZE = 1000;
const LINES_MAX_PAGES_PER_CHUNK = 50;
const EVENTS_PAGE_SIZE = 1000;
const EVENTS_MAX_PAGES = 50;
const DERIVED_FACTS_PAGE_SIZE = 500;

async function fetchAllEntities(entityType: string): Promise<Record<string, unknown>[]> {
  return fetchAllPages(
    async (page) => {
      const r = await twinService.listEntities(entityType, undefined, { page, limit: EVENTS_PAGE_SIZE });
      return { items: r.entities ?? [], hasMore: r.has_more ?? false };
    },
    { pageCap: EVENTS_MAX_PAGES, onCapExceeded: 'truncate', stopOnEmptyBatch: true },
  );
}

async function fetchAllLinesForChunk(eventIds: string[]): Promise<Record<string, unknown>[]> {
  return fetchAllPages(
    async (page) => {
      const r = await twinService.queryEntities('event_line', {
        attr_in: { event_id: eventIds },
        page,
        limit: LINES_PAGE_SIZE,
      });
      return { items: r.entities ?? [], hasMore: r.has_more ?? false };
    },
    { pageCap: LINES_MAX_PAGES_PER_CHUNK, onCapExceeded: 'truncate', stopOnEmptyBatch: true },
  );
}

/**
 * Project ``line_pairs`` + ``orphan_lines`` into per-line maps the
 * row builder consumes. Pairs flip matched/quantity_mismatch; orphans
 * stay unmatched (the default). Both sides of each pair carry the
 * same status so a UI row can read it without knowing which side it
 * came from.
 *
 * #1291 + #1391 N:M fan-out — a line on EITHER side may appear in N
 * pairs (delivery: 1 LS → N GS, #1291; settlement: N LS → 1 GS,
 * #1391), so:
 *   - ``pairedLinesByLine`` collects EVERY counterpart in append
 *     order (the pre-#1291 singular map was last-write-wins and
 *     silently dropped all but the final pair);
 *   - the mechanical status is worst-of: ANY ``qty_mismatch_paired``
 *     pair ambers the line. The post-kolli-fold aggregate passes
 *     (``applyDeliveryLineSumAggregates`` / ``applySettlementLineSumAggregates``)
 *     may upgrade a ≥2-pair line back to ``matched`` when the
 *     counterpart quantity SUM reconciles.
 */
function buildLineLookups(
  linePairs: GutschriftMatchGroupFactValue['line_pairs'],
  orphanLines: GutschriftMatchGroupFactValue['orphan_lines'],
): {
  statusByLine: Map<string, MatchLineStatus>;
  pairedLinesByLine: Map<string, string[]>;
  qtyDeltaByLine: Map<string, number | null>;
} {
  const statusByLine = new Map<string, MatchLineStatus>();
  const pairedLinesByLine = new Map<string, string[]>();
  const qtyDeltaByLine = new Map<string, number | null>();
  const setWorstOf = (lineId: string, status: MatchLineStatus) => {
    const held = statusByLine.get(lineId);
    // HOF-215 — ranked rather than a single "already amber?" guard. The
    // EUR 907,50 shape pairs TWO settlement lines to one delivery line, one of
    // them uncredited, so the delivery line is told ``matched`` by one pair and
    // ``price_missing`` by the other. The guard this replaces kept whichever
    // arrived first unless it was ``quantity_mismatch``, which would have
    // dropped the money signal on exactly that group.
    if (held !== undefined && LINE_STATUS_RANK[held] >= LINE_STATUS_RANK[status]) return;
    statusByLine.set(lineId, status);
  };
  const appendCounterpart = (lineId: string, counterpartId: string) => {
    const arr = pairedLinesByLine.get(lineId) ?? [];
    if (!arr.includes(counterpartId)) arr.push(counterpartId);
    pairedLinesByLine.set(lineId, arr);
  };
  for (const pair of linePairs) {
    const status: MatchLineStatus =
      pair.match_kind === 'price_missing_paired'
        ? 'price_missing'
        : pair.match_kind === 'qty_mismatch_paired'
          ? 'quantity_mismatch'
          : 'matched';
    const dl = pair.delivery_line_id != null ? String(pair.delivery_line_id) : null;
    const sl = pair.settlement_line_id != null ? String(pair.settlement_line_id) : null;
    if (dl) {
      setWorstOf(dl, status);
      qtyDeltaByLine.set(dl, pair.quantity_delta);
      if (sl) appendCounterpart(dl, sl);
    }
    if (sl) {
      setWorstOf(sl, status);
      qtyDeltaByLine.set(sl, pair.quantity_delta);
      if (dl) appendCounterpart(sl, dl);
    }
  }
  for (const orph of orphanLines) {
    if (orph.line_id) statusByLine.set(String(orph.line_id), 'unmatched');
  }
  return { statusByLine, pairedLinesByLine, qtyDeltaByLine };
}

/**
 * Strip the optional ``"D "``/``"D"`` prefix on a POS/D-number so the
 * raw extractor output (``"D 503685/2026"``) compares equal to the
 * canonical form (``"503685/2026"``). Mirror of the worker-side
 * ``_normalize_pos_token`` in
 * ``worker/src/services/materialisers/gutschrift_match_group.py`` —
 * duplicated locally because the FE needs the same canonical-key
 * mapping to align per-line ``referencedSettlementPos`` with the
 * group-level ``expectedSettlementPosTokens`` set (#793 phase 2). The
 * two helpers must stay in lockstep across the layer boundary.
 */
function normalizePosToken(raw: string): string {
  const s = raw.trim();
  if (s.toUpperCase().startsWith('D ')) return s.slice(2).trim();
  if (s.length > 1 && s[0].toUpperCase() === 'D' && /\d/.test(s[1])) return s.slice(1).trim();
  return s;
}

function toLine(
  raw: Record<string, unknown>,
  side: 'delivery' | 'settlement',
  lookups: ReturnType<typeof buildLineLookups>,
): MatchGroupLine {
  const lineId = String(raw.entity_id ?? raw.line_id ?? '');
  const rawPos = raw.referenced_settlement_pos;
  const matchedLineIds = lookups.pairedLinesByLine.get(lineId) ?? [];
  return {
    lineId,
    eventId: String(raw.event_id ?? ''),
    side,
    productName: String(raw.product_name ?? raw.name ?? ''),
    quantity: raw.quantity != null ? Number(raw.quantity) : null,
    quantityUnit: raw.quantity_unit != null ? String(raw.quantity_unit) : null,
    unitPrice: raw.unit_price != null ? Number(raw.unit_price) : null,
    amount: raw.amount != null ? Number(raw.amount) : null,
    matchStatus: lookups.statusByLine.get(lineId) ?? 'unmatched',
    matchedLineId: matchedLineIds[0] ?? null,
    matchedLineIds,
    quantityDelta: lookups.qtyDeltaByLine.get(lineId) ?? null,
    resourceCategory: raw.resource_category != null ? String(raw.resource_category) : null,
    rate: raw.rate != null ? Number(raw.rate) : null,
    palletCount: raw.pallet_count != null ? Number(raw.pallet_count) : null,
    packaging: raw.packaging != null ? String(raw.packaging) : null,
    resolvedKolliCount: null,
    resolvedKolliUnit: null,
    // #793 phase 2 — only LS-side lines carry the back-edge token in
    // their extractor output (``harvest-delivery-note.yaml``
    // ``line_items.referenced_settlement_pos``); settlement lines
    // never do. Normalise so MatchGraph can compare per-line tokens
    // against the group-level ``expectedSettlementPosTokens`` set
    // without further massage.
    referencedSettlementPos:
      side === 'delivery' && typeof rawPos === 'string' && rawPos.trim().length > 0
        ? normalizePosToken(rawPos)
        : null,
  };
}

/**
 * FRUTANiA-shaped Gutschriften split a single produce row across two
 * event_line entities. Fold the packaging line's quantity onto the
 * goods line and mark the packaging line "consumed" so it doesn't
 * double-render. Idempotent; lines without a matching pair are
 * untouched. Same logic as pre-sub-13; only the input shape (already
 * a ``MatchGroupLine[]``) is unchanged.
 */
function enrichGoodsWithPackagingKolli(lines: MatchGroupLine[]): Set<string> {
  const normalise = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
  const packagingByKey = new Map<string, MatchGroupLine>();
  for (const l of lines) {
    if (l.resourceCategory !== 'packaging') continue;
    const raw = l.packaging || l.productName;
    const key = normalise(raw);
    if (key) packagingByKey.set(key, l);
  }
  const consumed = new Set<string>();
  for (const l of lines) {
    if (l.resourceCategory !== 'goods') continue;
    if (!l.packaging) continue;
    const key = normalise(l.packaging);
    const carrier = packagingByKey.get(key);
    if (!carrier) continue;
    l.resolvedKolliCount = carrier.quantity;
    const carrierUnit = (carrier.quantityUnit ?? '').toLowerCase();
    l.resolvedKolliUnit =
      carrierUnit === '' || carrierUnit === 'stueck' || carrierUnit === 'stück'
        ? 'Kolli'
        : carrier.quantityUnit;
    consumed.add(carrier.lineId);
  }
  return consumed;
}

/**
 * #1291 N:1 fan-out — per-delivery-line SUM aggregation. Runs AFTER
 * ``enrichGoodsWithPackagingKolli`` on the post-fold, filtered line
 * lists (i.e. exactly what the canvas renders), mutating in place
 * (same style as the fold).
 *
 * Scope fence: ONLY delivery lines with ≥ 2 pairs are touched —
 * single-pair lines keep pre-#1291 behaviour bit-for-bit (including
 * the deliberate non-upgrade of a 1:1 mismatch pair whose quantities
 * happen to agree — unit-drift cases stay amber, plan risk #3).
 *
 * For a ≥2-pair delivery line whose counterparts all resolve and
 * carry quantities:
 *   - ``quantityDelta = Σ settlement.qty − delivery.qty`` (positive =
 *     over-credited), where each side's qty is the post-fold
 *     EFFECTIVE count: ``resolvedKolliCount ?? quantity``. A folded
 *     goods line keeps ``quantity`` in pallets while the canvas
 *     renders the Kolli count — comparing raw Pal against Kolli
 *     would mis-amber reconciled lines (codex r2).
 *   - upgrade-only: a ``quantity_mismatch`` line whose sum reconciles
 *     becomes ``matched``. Exact-kind lines are NEVER re-ambered —
 *     manual links are operator assertions (Wiesenhof 360 Kolli +
 *     10 Karton ≠ 288 must stay green).
 *   - every paired settlement line inherits the delivery line's
 *     aggregate status so both line spots and the fan-out edges
 *     render one coherent colour.
 *
 * Skips (leaves the line fully untouched) when any counterpart id
 * doesn't resolve — e.g. a pair referencing a packaging carrier line
 * that the kolli fold consumed — or when any quantity is null:
 * summing a partial subset would misstate the delta.
 */
/**
 * Post-fold EFFECTIVE count for a match-group line — EXACTLY what the
 * canvas renders for the line, mirroring ``LineRow``'s quantity column
 * (codex r3): a line whose own ``quantity`` is in a countable (non-Pal)
 * unit renders that directly (FRUTANiA shape A — the carrier count, if
 * any, is auxiliary); only a Pal-quantity (or quantity-less) line
 * renders the folded carrier count (shape B).
 *
 * Shared by ``applyDeliveryLineSumAggregates`` (the hook's aggregate
 * pass) AND ``computeDashboardTotals`` §3 (the FilterBar N:1
 * qty-mismatch split, #1305) — they MUST agree on what a line's
 * quantity is. Keeping one exported helper removes the "stay in
 * lockstep" fragility of two duplicated literals. If ``LineRow``'s
 * fallback chain changes, change this too.
 */
export function effectiveLineQty(l: MatchGroupLine): number | null {
  if (l.quantity != null && l.quantityUnit && l.quantityUnit.toLowerCase() !== 'pal') {
    return l.quantity;
  }
  if (l.resolvedKolliCount != null) return l.resolvedKolliCount;
  return l.quantity;
}

/**
 * #1444 — connected-component SUM-aggregate pass. Supersedes the two
 * sequential per-side passes (delivery-then-settlement) that #1291/#1391
 * ran, which were pass-order dependent: a settlement counterpart that is
 * independently ``quantity_mismatch`` could be cleared back to ``matched``
 * by the delivery pass and then never re-ambered, so a true N:M
 * cross-linked component rendered false-green and its delta euros dropped
 * out of the Abweichung dashboard split.
 *
 * This pass resolves the WHOLE bipartite connected component (delivery ↔
 * settlement, edges = ``matchedLineIds``) before assigning any status, so
 * the outcome is independent of which side is visited first.
 *
 * Per ≥ 2-pair pivot (either side) whose counterparts all resolve and
 * carry quantities, ``quantityDelta = Σ counterpart.qty − pivot.qty`` on
 * effective (kolli-folded) quantities — set on the pivot exactly as the
 * old per-side passes did. A pivot whose id doesn't resolve a counterpart
 * (e.g. a kolli-fold-consumed carrier) or whose quantities include a null
 * is left fully untouched (summing a partial subset would misstate the
 * delta).
 *
 * Worst-of component status (the #1444 fix):
 *   - if ANY evaluable ``quantity_mismatch`` pivot in the component has a
 *     non-reconciling aggregate, the component is unreconciled. Every
 *     ``quantity_mismatch`` pivot's fan-out (pivot + counterparts) is
 *     ambered to ``quantity_mismatch`` so spots + fan-out edges render one
 *     coherent colour — and crucially NOTHING is cleared back to
 *     ``matched``, so a cross-linked counterpart whose own aggregate
 *     reconciles stays ambered if a sibling pivot's aggregate fails;
 *   - otherwise (every evaluable aggregate reconciles) each aggregate's
 *     ``quantity_mismatch`` members (pivot + counterparts) are upgraded to
 *     ``matched``.
 *
 * Exact-kind (``matched``) pivots are NEVER re-ambered — a manual exact
 * link is an operator assertion, so its disagreeing delta is tolerated and
 * does not flip the component unreconciled (mirrors the prior upgrade-only
 * rule). But a ``matched`` counterpart of a FAILING ``quantity_mismatch``
 * pivot IS ambered, as before — the failing pivot's amber propagates onto
 * its whole fan-out.
 */
export function applyLineSumComponentAggregates(
  deliveryLines: MatchGroupLine[],
  settlementLines: MatchGroupLine[],
): void {
  const lineById = new Map<string, MatchGroupLine>();
  for (const l of deliveryLines) lineById.set(l.lineId, l);
  for (const l of settlementLines) lineById.set(l.lineId, l);
  const effectiveQty = effectiveLineQty;

  // Pass 1 — evaluate every ≥ 2-pair pivot's sub-aggregate. Set
  // ``quantityDelta`` (unconditional, as before) and record per-pivot
  // whether the sum reconciles. Track the counterpart ids each evaluable
  // pivot touches so reconciled components upgrade their counterparts too.
  // ``pivotWasExact`` snapshots the pivot's status BEFORE any pass-2
  // mutation. The assignment loop must gate on this snapshot, not the live
  // ``pivot.matchStatus``: in an unreconciled component a failing sibling
  // pivot ambers an exact ``matched`` multi-pair pivot, and reading the
  // (now-ambered) live status would make THAT pivot re-propagate amber onto
  // its own exact counterparts — reintroducing the order dependence this
  // pass exists to remove (#1444 codex r1 P2).
  type Aggregate = {
    pivot: MatchGroupLine;
    pivotWasExact: boolean;
    reconciles: boolean;
    memberIds: string[];
  };
  const aggregates: Aggregate[] = [];
  for (const pivot of lineById.values()) {
    if (pivot.matchedLineIds.length < 2) continue; // safety fence — 1:1 untouched
    const counterparts = pivot.matchedLineIds.map((id) => lineById.get(id));
    if (counterparts.some((c) => c === undefined)) continue;
    const resolved = counterparts as MatchGroupLine[];
    const pivotQty = effectiveQty(pivot);
    if (pivotQty == null || resolved.some((c) => effectiveQty(c) == null)) continue;
    const delta = resolved.reduce((acc, c) => acc + (effectiveQty(c) as number), 0) - pivotQty;
    pivot.quantityDelta = delta;
    aggregates.push({
      pivot,
      // A ``price_missing`` pivot counts as exact HERE, deliberately: this
      // snapshot decides whether the pivot may amber its own fan-out on a
      // QUANTITY failure, and a price deviation is not evidence about
      // quantities. The line keeps its own status either way (see the
      // assignment loop below).
      pivotWasExact: pivot.matchStatus !== 'quantity_mismatch',
      reconciles: Math.abs(delta) < 1e-6,
      memberIds: [pivot.lineId, ...resolved.map((c) => c.lineId)],
    });
  }

  if (aggregates.length === 0) return; // no ≥ 2-pair aggregate — nothing to do

  // Pass 2 — union-find over ``matchedLineIds`` to group lines into
  // connected components, then decide each component's status worst-of.
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    let root = x;
    while (parent.get(root) !== root) root = parent.get(root) as string;
    // Path-compress.
    let cur = x;
    while (parent.get(cur) !== root) {
      const next = parent.get(cur) as string;
      parent.set(cur, root);
      cur = next;
    }
    return root;
  };
  const union = (a: string, b: string) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  for (const id of lineById.keys()) if (!parent.has(id)) parent.set(id, id);
  for (const l of lineById.values()) {
    for (const cid of l.matchedLineIds) {
      if (lineById.has(cid)) union(l.lineId, cid);
    }
  }

  // A component is unreconciled if any of its evaluable ``quantity_mismatch``
  // pivots has a non-reconciling aggregate. Exact-kind (``matched``) pivots
  // are tolerated (upgrade-only) and never flip the component unreconciled.
  const unreconciledRoots = new Set<string>();
  for (const agg of aggregates) {
    if (!agg.pivotWasExact && !agg.reconciles) {
      unreconciledRoots.add(find(agg.pivot.lineId));
    }
  }

  // Assign status worst-of, AFTER the component verdict is known — so the
  // outcome is independent of pivot/pass order.
  for (const agg of aggregates) {
    if (unreconciledRoots.has(find(agg.pivot.lineId))) {
      // Failing component: a ``quantity_mismatch`` pivot ambers its whole
      // fan-out (pivot + counterparts) so spots + edges agree. A
      // ``matched`` exact-kind pivot is tolerated — it does not amber its
      // own fan-out (but its counterparts may still be ambered by another
      // failing pivot in the same component). Gate on the PRE-mutation
      // snapshot so a pivot ambered by a sibling earlier in this loop does
      // not then re-propagate that amber. Never clear to ``matched``.
      if (agg.pivotWasExact) continue;
      for (const memberId of agg.memberIds) {
        const member = lineById.get(memberId);
        // HOF-215 — never downgrade a ``price_missing`` member to
        // ``quantity_mismatch``. Both are deviations, but only one names the
        // money, and this pass is about quantities.
        if (member && member.matchStatus !== 'price_missing') member.matchStatus = 'quantity_mismatch';
      }
    } else {
      // Reconciled component: upgrade every ``quantity_mismatch`` member
      // (pivot + counterparts) to ``matched``.
      for (const memberId of agg.memberIds) {
        const member = lineById.get(memberId);
        if (member && member.matchStatus === 'quantity_mismatch') {
          member.matchStatus = 'matched';
        }
      }
    }
  }
}

/**
 * Generic SUM-aggregate pass kept for the per-side unit suites and any
 * direct caller. ``pivotLines`` are the fan-out side; ``counterpartLines``
 * the OTHER side. Delegates to the component-level pass (#1444) so the
 * outcome is pass-order independent — passing one side's pivots plus the
 * full counterpart set is equivalent to the composer's whole-group call,
 * because only ≥ 2-pair pivots are ever evaluated.
 */
function applyLineSumAggregates(
  pivotLines: MatchGroupLine[],
  counterpartLines: MatchGroupLine[],
  pivotSide: 'delivery' | 'settlement',
): void {
  if (pivotSide === 'delivery') {
    applyLineSumComponentAggregates(pivotLines, counterpartLines);
  } else {
    applyLineSumComponentAggregates(counterpartLines, pivotLines);
  }
}

export function applyDeliveryLineSumAggregates(
  deliveryLines: MatchGroupLine[],
  settlementLines: MatchGroupLine[],
): void {
  applyLineSumAggregates(deliveryLines, settlementLines, 'delivery');
}

/**
 * #1391 N:1 settlement fan-out — per-settlement-line SUM aggregation,
 * the mirror of ``applyDeliveryLineSumAggregates``. A Gutschrift line
 * may aggregate several Lieferschein lines (Nicole's prod repro: 3
 * Lieferscheine → 1 GS). Delegates to the same component-level pass.
 */
export function applySettlementLineSumAggregates(
  deliveryLines: MatchGroupLine[],
  settlementLines: MatchGroupLine[],
): void {
  applyLineSumAggregates(settlementLines, deliveryLines, 'settlement');
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useMatchGroups(tenantMonth: string) {
  // 1. Unfiltered derived-facts read — gives the structural decisions
  // for every month, lets us derive ``availableMonths`` in one shot.
  // Volume bound: one fact per (agreement, period_ym, event_set). For
  // a year-long Frutania run that's ~120 rows — well under the 500
  // limit, no pagination needed today. Bump to a loop if/when that
  // assumption breaks.
  const factsQuery = useQuery({
    queryKey: [...matchGroupKeys.all, 'derived-facts'],
    queryFn: () =>
      twinService.queryDerivedFacts({
        fact_type: 'gutschrift_match_group',
        limit: DERIVED_FACTS_PAGE_SIZE,
      }),
    enabled: Boolean(tenantMonth),
    staleTime: 30_000,
  });

  const allFacts: DerivedFact[] = factsQuery.data?.derived_facts ?? [];

  // Defensive dedupe by ``group_id`` — content-addressing means the
  // server shouldn't emit duplicates, but a future paginated read
  // could touch the same group on consecutive pages.
  const dedupedFacts: DerivedFact[] = (() => {
    const seen = new Set<string>();
    const out: DerivedFact[] = [];
    for (const df of allFacts) {
      const fv = df.fact_value as unknown as GutschriftMatchGroupFactValue;
      if (!fv?.group_id || seen.has(fv.group_id)) continue;
      seen.add(fv.group_id);
      out.push(df);
    }
    return out;
  })();

  const availableMonths = Array.from(
    new Set(
      dedupedFacts
        .map((df) => (df.fact_value as unknown as GutschriftMatchGroupFactValue).period_ym)
        .filter((ym): ym is string => typeof ym === 'string' && ym.length > 0),
    ),
  ).sort();

  // #1287 — distinct Gutschrift counts per period for the lens-panel
  // badges, derived in the same unfiltered pass as ``availableMonths``
  // (no extra fetch). Month badge = distinct ``settlement_event_ids``
  // within that ``period_ym``; year badge = distinct UNION across the
  // year's month sets — NOT the sum of month counts, which would
  // double-count an id that appears in two months. A month holding
  // only orphan_delivery groups has no settlement ids and reports 0
  // (the LensePanel renders no badge for 0).
  const gsIdsByMonth = new Map<string, Set<string>>();
  for (const df of dedupedFacts) {
    const fv = df.fact_value as unknown as GutschriftMatchGroupFactValue;
    if (typeof fv.period_ym !== 'string' || fv.period_ym.length === 0) continue;
    const monthSet = gsIdsByMonth.get(fv.period_ym) ?? new Set<string>();
    for (const id of fv.settlement_event_ids ?? []) monthSet.add(id);
    gsIdsByMonth.set(fv.period_ym, monthSet);
  }
  const gsCountsByMonth: Record<string, number> = {};
  const gsIdsByYear = new Map<string, Set<string>>();
  // #1446 — all-season distinct union of every fact's settlement ids,
  // for the Gesamt season badge. Built in the SAME pass as the per-year
  // sets so it sees the identical id source. Since #1389 cross-month
  // matching, one ``settlement_event_id`` can land in facts under two
  // different ``period_ym`` years; each year-union counts it, so summing
  // the per-year unions double-counts it. This single union dedupes it
  // across the year boundary, so the badge reports the true distinct count.
  const seasonGsIds = new Set<string>();
  gsIdsByMonth.forEach((ids, ym) => {
    gsCountsByMonth[ym] = ids.size;
    const year = ym.slice(0, 4);
    const yearSet = gsIdsByYear.get(year) ?? new Set<string>();
    ids.forEach((id) => {
      yearSet.add(id);
      seasonGsIds.add(id);
    });
    gsIdsByYear.set(year, yearSet);
  });
  const gsCountsByYear: Record<string, number> = {};
  gsIdsByYear.forEach((ids, year) => {
    gsCountsByYear[year] = ids.size;
  });
  const seasonGsCount = seasonGsIds.size;

  // #1804 — the Gesamt season mode is dropped (option (a)): ``tenantMonth`` is
  // ALWAYS a concrete ``YYYY-MM`` now (both pages redirect a legacy
  // ``?ym=gesamt`` to the latest month BEFORE calling this hook), so the
  // ``period_ym`` filter runs unconditionally. To restore the season roll-up
  // (option (b)) revert this together with the feed's season branches.
  const monthFacts = dedupedFacts.filter(
    (df) => (df.fact_value as unknown as GutschriftMatchGroupFactValue).period_ym === tenantMonth,
  );

  // 2. Hydrate events. Same listEntities pattern as pre-sub-13; the
  // perf win sub-13 delivers is from skipping all the join/dedup/
  // status-derivation work above, not from changing entity fetches.
  // TODO(#632 follow-up): replace with attr_in on entity_id once the
  // entities query supports primary-key IN-lists, so we fetch only
  // the events in groups rather than the full tenant set.
  const settlementQuery = useQuery({
    queryKey: [...matchGroupKeys.all, 'settlement-events'],
    queryFn: () => fetchAllEntities('settlement_event'),
    enabled: monthFacts.length > 0,
    staleTime: 60_000,
  });
  const deliveryQuery = useQuery({
    queryKey: [...matchGroupKeys.all, 'delivery-events'],
    queryFn: () => fetchAllEntities('delivery_event'),
    enabled: monthFacts.length > 0,
    staleTime: 60_000,
  });
  // #784: hydrate ``sales_agreement`` rows so the BESTELLUNG column can
  // render each agreement's ``agreement_number`` (BestellNr) and friendly
  // ``name``. One paginated fetch per tenant — identical pattern to the
  // event-side queries above. Lex-cheaper than per-group GETs because
  // multiple groups share the same agreement (one Bestellung receives
  // many LS + GS pairs over its lifetime).
  //
  // Disabled when no group references an agreement (zero-agreement
  // tenants pay no fetch). The check fires before the per-group join
  // so the query disables cleanly even when ``monthFacts`` is non-empty.
  // Covers BOTH the canonical plural ``agreement_ids`` and the legacy
  // singleton ``agreement_id`` so tenants whose facts haven't been
  // re-materialised yet still trigger the fetch.
  const anyAgreement = monthFacts.some((df) => {
    const fv = df.fact_value as unknown as GutschriftMatchGroupFactValue;
    return (fv.agreement_ids?.length ?? 0) > 0 || !!fv.agreement_id;
  });
  const agreementQuery = useQuery({
    queryKey: [...matchGroupKeys.all, 'sales-agreements'],
    queryFn: () => fetchAllEntities('sales_agreement'),
    enabled: monthFacts.length > 0 && anyAgreement,
    staleTime: 60_000,
  });

  // #793 phase 4 — hydrate Abrechnung + Zahlungsavis entities. Same
  // tenant-wide paginated pattern as the agreement query above: multiple
  // groups in a month frequently share the same Abrechnung (one
  // Periodenabrechnung covers N GS within the billing period), so a
  // tenant-wide fetch is cheaper than per-group GETs.
  //
  // ``enabled`` gates on any group referencing the entity so a tenant
  // with no Abrechnung / no Zahlungsavis pays no fetch. Note the
  // Abrechnung query also fires when there's an unmatched
  // ``expected_abrechnungs_numbers`` (GS references a number we can't
  // match) — MatchGraph still wants to know the live entity list before
  // emitting placeholders, otherwise a race between this query and the
  // facts query would briefly mis-render a real Abrechnung as a
  // missing-doc placeholder.
  const anyPayoutStatement = monthFacts.some((df) => {
    const fv = df.fact_value as unknown as GutschriftMatchGroupFactValue;
    return (
      (fv.payout_statement_ids?.length ?? 0) > 0 ||
      (fv.expected_abrechnungs_numbers?.length ?? 0) > 0
    );
  });
  const payoutStatementQuery = useQuery({
    queryKey: [...matchGroupKeys.all, 'payout-statement-events'],
    queryFn: () => fetchAllEntities('payout_statement_event'),
    enabled: monthFacts.length > 0 && anyPayoutStatement,
    staleTime: 60_000,
  });
  const anyPayoutAdvice = monthFacts.some((df) => {
    const fv = df.fact_value as unknown as GutschriftMatchGroupFactValue;
    return (fv.payout_advice_ids?.length ?? 0) > 0;
  });
  const payoutAdviceQuery = useQuery({
    queryKey: [...matchGroupKeys.all, 'payout-advice-events'],
    queryFn: () => fetchAllEntities('payout_advice_event'),
    enabled: monthFacts.length > 0 && anyPayoutAdvice,
    staleTime: 60_000,
  });

  const settlementById = new Map<string, Record<string, unknown>>();
  for (const e of settlementQuery.data ?? []) {
    settlementById.set(String(e.entity_id ?? ''), e);
  }
  const deliveryById = new Map<string, Record<string, unknown>>();
  for (const e of deliveryQuery.data ?? []) {
    deliveryById.set(String(e.entity_id ?? ''), e);
  }
  const agreementById = new Map<string, Record<string, unknown>>();
  for (const e of agreementQuery.data ?? []) {
    agreementById.set(String(e.entity_id ?? ''), e);
  }
  const payoutStatementById = new Map<string, Record<string, unknown>>();
  for (const e of payoutStatementQuery.data ?? []) {
    payoutStatementById.set(String(e.entity_id ?? ''), e);
  }
  const payoutAdviceById = new Map<string, Record<string, unknown>>();
  for (const e of payoutAdviceQuery.data ?? []) {
    payoutAdviceById.set(String(e.entity_id ?? ''), e);
  }

  // 3. Collect event_ids referenced by the visible-month facts only.
  const allEventIds = (() => {
    const ids = new Set<string>();
    for (const df of monthFacts) {
      const fv = df.fact_value as unknown as GutschriftMatchGroupFactValue;
      for (const id of fv.settlement_event_ids ?? []) ids.add(id);
      for (const id of fv.delivery_event_ids ?? []) ids.add(id);
    }
    return Array.from(ids).sort();
  })();

  // 4. Batch-fetch event_lines via attr_in. Single round trip per chunk
  // of 200 event_ids — replaces the N+1 GET-per-event pattern from
  // pre-#673. Codex PR #773 P2: a chunk with >1000 lines used to be
  // silently truncated to the first page because the query didn't
  // follow ``has_more``. Each chunk now paginates until the server
  // signals exhaustion (or ``LINES_MAX_PAGES_PER_CHUNK`` is hit as a
  // safety stop, mirroring ``fetchAllEntities`` for events).
  const linesQuery = useQuery({
    queryKey: [...matchGroupKeys.byMonth(tenantMonth), 'lines', allEventIds.join(',')],
    queryFn: async () => {
      if (allEventIds.length === 0) return [];
      const chunks: Promise<Record<string, unknown>[]>[] = [];
      for (let i = 0; i < allEventIds.length; i += LINES_BATCH_SIZE) {
        const chunk = allEventIds.slice(i, i + LINES_BATCH_SIZE);
        chunks.push(fetchAllLinesForChunk(chunk));
      }
      const results = await Promise.all(chunks);
      return results.flat();
    },
    enabled: allEventIds.length > 0,
  });

  const linesByEvent = new Map<string, Record<string, unknown>[]>();
  for (const line of linesQuery.data ?? []) {
    const eid = String(line.event_id ?? '');
    if (!eid) continue;
    const bucket = linesByEvent.get(eid) ?? [];
    bucket.push(line);
    linesByEvent.set(eid, bucket);
  }

  // 5. Compose rich MatchGroup objects.
  //
  // Multi-event groups (multiple GS consolidating to one LS, or vice
  // versa) collapse to the lex-smallest event on each side — the
  // materialiser's "primary entity" convention. v1 deviation: the
  // rich shape only carries one event per side; rendering multiple
  // events of the same side in one card is a future-shape extension
  // (see sub-13 Work Log).
  const groups: MatchGroup[] = monthFacts.map((df) => {
    const fv = df.fact_value as unknown as GutschriftMatchGroupFactValue;
    const seId = fv.settlement_event_ids?.[0] ?? null;
    const deId = fv.delivery_event_ids?.[0] ?? null;
    const settlementEvent = seId ? settlementById.get(seId) ?? null : null;
    const deliveryEvent = deId ? deliveryById.get(deId) ?? null : null;

    const lookups = buildLineLookups(fv.line_pairs ?? [], fv.orphan_lines ?? []);

    const seLinesAll = (fv.settlement_event_ids ?? [])
      .flatMap((eid) => linesByEvent.get(eid) ?? [])
      .map((l) => toLine(l, 'settlement', lookups));
    const consumedSe = enrichGoodsWithPackagingKolli(seLinesAll);
    const settlementLines = seLinesAll.filter((l) => !consumedSe.has(l.lineId));

    const deLinesAll = (fv.delivery_event_ids ?? [])
      .flatMap((eid) => linesByEvent.get(eid) ?? [])
      .map((l) => toLine(l, 'delivery', lookups));
    const consumedDe = enrichGoodsWithPackagingKolli(deLinesAll);
    const deliveryLines = deLinesAll.filter((l) => !consumedDe.has(l.lineId));

    // #1291 + #1391 + #1444 — N:M SUM aggregation runs after BOTH kolli
    // folds so it sees the post-fold quantities + line identity the canvas
    // renders. ONE connected-component pass (delivery-pivot fan-out, #1291:
    // 1 LS → N GS, AND settlement-pivot fan-out, #1391: N LS → 1 GS) instead
    // of the two sequential per-side passes — so a cross-linked counterpart
    // ambered by its own aggregate is never cleared back to matched by the
    // other side's pivot (pass-order independence, #1444).
    applyLineSumComponentAggregates(deliveryLines, settlementLines);

    const allSettlementEvents = (fv.settlement_event_ids ?? [])
      .map((eid) => settlementById.get(eid))
      .filter((e): e is Record<string, unknown> => !!e);
    const allDeliveryEvents = (fv.delivery_event_ids ?? [])
      .map((eid) => deliveryById.get(eid))
      .filter((e): e is Record<string, unknown> => !!e);

    // #784: hydrate BESTELLUNG column rows. Missing entities (id in
    // ``agreement_ids`` but no row in the agreement query — happens
    // when the agreement was tombstoned between fact write and hook
    // run) are dropped. Subtask 15 (#793 phase 1) covers the
    // *typed-placeholder* case where the id resolves to nothing.
    //
    // Legacy fall-back: facts written before the materialiser added
    // the plural ``agreement_ids`` list carry only the legacy
    // singleton ``agreement_id``. Fall back to ``[agreement_id]`` so
    // those tenants still see their BESTELLUNG column during the
    // rolling-window where re-materialisation hasn't reached every
    // group yet. Once a fact is recomputed both fields are present
    // and ``agreement_ids`` takes precedence.
    const agreementIds: string[] =
      fv.agreement_ids && fv.agreement_ids.length > 0
        ? fv.agreement_ids
        : fv.agreement_id
          ? [fv.agreement_id]
          : [];
    const bestellung: BestellungSummary[] = agreementIds
      .map((aid) => {
        const row = agreementById.get(aid);
        if (!row) return null;
        const num = row.agreement_number;
        const name = row.name;
        const supplierName = row.supplier_name;
        const buyerName = row.buyer_name;
        // #1812 (FE) — SINGULAR order-doc provenance only. NEVER
        // ``row.source_doc_ids`` (plural, parent ``agreement.yaml``):
        // stub-minted rows pollute it with the citing GS/LS doc, so
        // reading it would open the wrong document in the preview.
        const sourceDocId = row.source_doc_id;
        return {
          entityId: aid,
          agreementNumber: typeof num === 'string' && num.length > 0 ? num : null,
          name: typeof name === 'string' && name.length > 0 ? name : null,
          supplierName: typeof supplierName === 'string' && supplierName.length > 0 ? supplierName : null,
          buyerName: typeof buyerName === 'string' && buyerName.length > 0 ? buyerName : null,
          sourceDocId: typeof sourceDocId === 'string' && sourceDocId.length > 0 ? sourceDocId : null,
        } satisfies BestellungSummary;
      })
      .filter((s): s is BestellungSummary => s !== null);

    // #793 phase 4 — hydrate Abrechnung column rows. Missing entities
    // (id in ``payout_statement_ids`` but no row in the payout-statement
    // query — happens when the entity was tombstoned between fact
    // write and hook run) are dropped. The strict-typed placeholder
    // path (``expectedAbrechnungsNumbers`` set, no matching row) is
    // handled FE-side by MatchGraph diffing the two arrays.
    const payoutStatements: PayoutStatementSummary[] = (fv.payout_statement_ids ?? [])
      .map((pid) => {
        const row = payoutStatementById.get(pid);
        if (!row) return null;
        const number = row.number;
        const totalAmount = row.total_amount;
        const periodStart = row.period_start;
        const periodEnd = row.period_end;
        const issuerName = row.issuer_name;
        const recipientName = row.recipient_name;
        return {
          entityId: pid,
          number: typeof number === 'string' && number.length > 0 ? number : null,
          totalAmount:
            typeof totalAmount === 'number'
              ? totalAmount
              : totalAmount != null && Number.isFinite(Number(totalAmount))
                ? Number(totalAmount)
                : null,
          periodStart: typeof periodStart === 'string' && periodStart.length > 0 ? periodStart : null,
          periodEnd: typeof periodEnd === 'string' && periodEnd.length > 0 ? periodEnd : null,
          issuerName: typeof issuerName === 'string' && issuerName.length > 0 ? issuerName : null,
          recipientName:
            typeof recipientName === 'string' && recipientName.length > 0 ? recipientName : null,
        } satisfies PayoutStatementSummary;
      })
      .filter((s): s is PayoutStatementSummary => s !== null);

    // #793 phase 4 — hydrate Zahlungsavis column rows. Symmetric to the
    // Abrechnung block above; the source-side ``number`` may be an
    // Avis-Nr. or a Konto-Nr. depending on sender (FRUTANiA 20260520
    // carries Konto, not Avis-Nr.) — both map to the same field.
    const payoutAdvices: PayoutAdviceSummary[] = (fv.payout_advice_ids ?? [])
      .map((pid) => {
        const row = payoutAdviceById.get(pid);
        if (!row) return null;
        const number = row.number;
        const totalPaymentAmount = row.total_payment_amount;
        const paymentDueDate = row.payment_due_date;
        const buyerName = row.buyer_name;
        const sellerName = row.seller_name;
        // #793 phase 4 codex P1 — extract document_number from each
        // entry in ``referenced_documents`` so MatchGraph can pick
        // the correct GS card as the arrow source in a multi-GS
        // component. The shape (per harvest-payment-advice.yaml) is
        // ``[{document_type, document_number, document_date, amount,
        // reference_label}, ...]``; defensively skip malformed rows.
        const rawRefs = row.referenced_documents;
        const referencedCreditNoteNumbers: string[] = Array.isArray(rawRefs)
          ? rawRefs
              .map((r) =>
                r && typeof r === 'object' && 'document_number' in r
                  ? String((r as Record<string, unknown>).document_number ?? '').trim()
                  : '',
              )
              .filter((n) => n.length > 0)
          : [];
        return {
          entityId: pid,
          number: typeof number === 'string' && number.length > 0 ? number : null,
          totalPaymentAmount:
            typeof totalPaymentAmount === 'number'
              ? totalPaymentAmount
              : totalPaymentAmount != null && Number.isFinite(Number(totalPaymentAmount))
                ? Number(totalPaymentAmount)
                : null,
          paymentDueDate:
            typeof paymentDueDate === 'string' && paymentDueDate.length > 0 ? paymentDueDate : null,
          buyerName: typeof buyerName === 'string' && buyerName.length > 0 ? buyerName : null,
          sellerName: typeof sellerName === 'string' && sellerName.length > 0 ? sellerName : null,
          referencedCreditNoteNumbers,
        } satisfies PayoutAdviceSummary;
      })
      .filter((s): s is PayoutAdviceSummary => s !== null);

    const payoutStatementConflicts: PayoutStatementConflict[] = (
      fv.payout_statement_conflicts ?? []
    ).map((c) => ({
      abrechnungsNumber: c.abrechnungs_number,
      payoutStatementIds: c.payout_statement_ids ?? [],
    }));

    return {
      id: fv.group_id,
      settlementEvent,
      deliveryEvent,
      settlementLines,
      deliveryLines,
      allSettlementEvents,
      allDeliveryEvents,
      bestellung,
      payoutStatements,
      expectedAbrechnungsNumbers: fv.expected_abrechnungs_numbers ?? [],
      payoutAdvices,
      payoutStatementConflicts,
      status: fv.status,
      // #1289 — null-safe: legacy facts pre-date the money block.
      money: fv.money
        ? {
            settlementTotal: fv.money.settlement_total,
            deliveryTotal: fv.money.delivery_total,
            delta: fv.money.delta,
            currency: fv.money.currency,
            // HOF-215 AC 6 — default FALSE, never undefined: a legacy fact
            // that pre-dates the flag is not making a claim, and rendering
            // "nicht vergleichbar" on it would hide a real delta.
            deliveryUnpriced: fv.money.delivery_unpriced === true,
          }
        : null,
      // HOF-215 AC 9 — the residue becomes visible. Empty for a healthy group.
      degradedReasons: fv.degraded_reasons ?? [],
      duplicateCount: 1,
      manuallyResolvedAt: fv.manually_resolved_at ?? null,
      // #793 phase 2 — empty list for legacy facts that pre-date the
      // materialiser surfacing this field. Tokens come pre-normalised
      // from the worker (sorted, deduped, prefix-stripped); the FE
      // consumes them as-is.
      expectedSettlementPosTokens: fv.expected_settlement_pos_tokens ?? [],
      // HOF-219 — pass-through, camelCased. Filtered to well-formed entries so
      // a truncated legacy row cannot put ``undefined`` into the confirmation
      // copy an operator is deciding on.
      gsLsEdges: (fv.gs_ls_edges ?? [])
        .filter((e) => !!e?.settlement_event_id && !!e?.delivery_event_id)
        .map((e) => ({
          settlementEventId: e.settlement_event_id,
          deliveryEventId: e.delivery_event_id,
          linkKind: e.link_kind,
        })),
    };
  });

  return {
    groups,
    availableMonths,
    gsCountsByMonth,
    gsCountsByYear,
    seasonGsCount,
    isLoading:
      factsQuery.isLoading ||
      settlementQuery.isLoading ||
      deliveryQuery.isLoading ||
      linesQuery.isLoading ||
      agreementQuery.isLoading ||
      payoutStatementQuery.isLoading ||
      payoutAdviceQuery.isLoading,
    error:
      factsQuery.error ??
      settlementQuery.error ??
      deliveryQuery.error ??
      linesQuery.error ??
      agreementQuery.error ??
      payoutStatementQuery.error ??
      payoutAdviceQuery.error,
  };
}
